import React, { useState, useCallback } from 'react';

export interface ColorSettingSliderSimpleProps {
  value: number;                    // 2700-6500 (color temperature in Kelvin)
  onChange: (value: number) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  label?: string;
  showValue?: boolean;
}

/**
 * ColorSetting Style 02 - Simple Slider (No Presets)
 * 
 * 色溫滑桿控制（無快捷按鈕）
 * - 2700K = 暖光 (偏黃)
 * - 4000K = 日光 (中性白)
 * - 6500K = 冷光 (偏藍)
 */
export const Style02_SliderSimple: React.FC<ColorSettingSliderSimpleProps> = ({
  value,
  onChange,
  disabled = false,
  min = 2700,
  max = 6500,
  label = '色溫',
  showValue = true,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  // Sync local value with prop when not dragging
  React.useEffect(() => {
    if (!isDragging) {
      setLocalValue(value);
    }
  }, [value, isDragging]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    setLocalValue(newValue);
  }, []);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    onChange(localValue);
  }, [localValue, onChange]);

  // Temperature to color mapping
  const getColorForTemp = (temp: number): string => {
    if (temp <= 3000) return '#ffb347'; // Warm orange-yellow
    if (temp <= 4000) return '#ffd699'; // Warm white
    if (temp <= 5000) return '#fff5e6'; // Neutral white
    if (temp <= 5500) return '#ffffff'; // Pure white
    return '#e6f3ff'; // Cool blue-white
  };

  // Get temperature label
  const getTempLabel = (temp: number): string => {
    if (temp <= 3200) return '暖光';
    if (temp <= 4500) return '日光';
    return '冷光';
  };

  const thumbColor = getColorForTemp(localValue);

  return (
    <div className="space-y-3">
      {/* Label and Value */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{label}</span>
        {showValue && (
          <div className="flex items-center gap-2">
            <div 
              className="w-4 h-4 rounded-full border border-slate-600"
              style={{ backgroundColor: thumbColor }}
            />
            <span className="text-lg font-bold text-white">
              {localValue}K
            </span>
            <span className="text-sm text-slate-400">
              ({getTempLabel(localValue)})
            </span>
          </div>
        )}
      </div>

      {/* Slider with gradient background */}
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          value={localValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleTouchEnd}
          disabled={disabled}
          className={`colortemp-slider w-full h-3 rounded-full cursor-pointer appearance-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{
            background: `linear-gradient(to right, 
              #ffb347 0%, 
              #ffd699 30%, 
              #ffffff 60%, 
              #e6f3ff 100%)`
          }}
        />
      </div>
    </div>
  );
};

export default Style02_SliderSimple;
